/**
 * Sign-out confirm — intentional exit like Notion/Stripe/GitHub:
 * calm copy, optional revoke-everywhere, pending label, no surprise.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { FOCUS_RING } from '@/components/focus';

type SignOutDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Prefer revoke-everywhere when the dialog opens (Account security). */
  defaultEverywhere?: boolean;
};

export function SignOutDialog({
  open,
  onClose,
  defaultEverywhere = false,
}: SignOutDialogProps) {
  const { t } = useTranslation();
  const { user, logout, isSigningOut } = useAuth();
  const [everywhere, setEverywhere] = useState(defaultEverywhere);

  useEffect(() => {
    if (open) setEverywhere(defaultEverywhere);
  }, [open, defaultEverywhere]);

  const confirm = () => {
    logout({ everywhere });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!isSigningOut) onClose();
      }}
      title={t('common.auth.signOutTitle')}
      actions={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSigningOut}
          >
            {t('common.auth.signOutCancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={confirm}
            disabled={isSigningOut}
            aria-busy={isSigningOut}
          >
            {isSigningOut ? (
              <>
                <Loader2 size={16} className="animate-spin" aria-hidden />
                {t('common.auth.signOutPending')}
              </>
            ) : (
              t('common.auth.signOutConfirm')
            )}
          </Button>
        </>
      }
    >
      <p className="text-tinta-2">{t('common.auth.signOutBody')}</p>
      {user?.email ? (
        <p className="mt-3 text-[13px] text-tinta-3">
          {t('common.auth.signOutAs', { email: user.email })}
        </p>
      ) : null}

      <label
        className={cn(
          'mt-5 flex cursor-pointer items-start gap-3 rounded-lg bg-superficie-2/60 px-3 py-3 transition-colors hover:bg-superficie-2',
          FOCUS_RING,
          isSigningOut && 'pointer-events-none opacity-60',
        )}
      >
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-linea-fuerte accent-[var(--acento)]"
          checked={everywhere}
          disabled={isSigningOut}
          onChange={(e) => setEverywhere(e.target.checked)}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-medium text-tinta">
            {t('common.auth.signOutEverywhere')}
          </span>
          <span className="mt-1 block text-[12px] leading-4 text-tinta-2">
            {t('common.auth.signOutEverywhereHint')}
          </span>
        </span>
      </label>
    </Modal>
  );
}
